import { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { MotiView } from "moti";
import TabNavigator from "../components/common/general/TabNavigator/TabNavigator";
import { useAppConfig } from "../AppConfigProvider";
import { RolePermissions } from "../constants/rolesAndPermissions";
import CourseInfoContainer from "../containers/courseSettings/CourseInfoContainer/CourseInfoContainer";
import DueDateControlsContainer from "../containers/courseSettings/DueDateControlsContainer/DueDateControlsContainer";
import CourseCatalogInfoContainer from "../containers/courseSettings/CourseCatalogInfoContainer/CourseCatalogInfoContainer";
import CourseAnnouncementsContainer from "../containers/courseSettings/CourseAnnouncementsContainer/CourseAnnouncementsContainer";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import RosterManagementContainer from "../containers/courseSettings/RosterManagementContainer/RosterManagementContainer";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAuth } from "../hooks/useAuth";

function CourseSettingsView() {
  const posthog = usePostHog();
  const { isMedium } = useResponsiveScreen();
  const { isAxioOffering, primaryColor } = useAppConfig();

  const {
    params: { courseId, tab },
  } = useRoute<RouteProp<RootStackParamList, "Course Settings">>();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [activeTab, setActiveTab] = useState(tab ?? "courseAnnouncements");

  useEffect(() => {
    setActiveTab(tab ?? "courseAnnouncements");
  }, [tab]);

  const { userInfo } = useAuth();

  const tabs = [
    { key: "courseAnnouncements", title: "Course Announcements" },
    { key: "courseInfo", title: "Course Info" },
    { key: "rosterManagement", title: "Roster Management" },
    { key: "dueDateControls", title: "Due Date Controls" },
    { key: "courseCatalogInfo", title: "Course Catalog Info" },
  ];

  useEffect(() => {
    posthog?.capture("Course Settings Page Visited");
  }, []);

  if (!userInfo.userPermissions.includes(RolePermissions.courseEdit)) {
    return (
      <DefaultLayout pageHasChat={false} showCourseNav={true}>
        <CustomText text="You do not have permission to access this page" />
      </DefaultLayout>
    );
  }

  const handleBackToCourseEditor = () => {
    navigation.navigate("Custom Coursework Modules", { courseId });
  };

  return (
    <DefaultLayout withPadding={true} pageHasChat={false} menuType="none" showCourseNav={true}>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        transition={{
          all: { type: "timing", duration: 500 },
        }}>
        <View style={styles.container}>
          <TouchableOpacity onPress={handleBackToCourseEditor}>
            <CustomText
              useTranslationText={false}
              size="s"
              text="< Back to Course Editor"
              style={styles.breadcrumbText}
            />
          </TouchableOpacity>
          <View style={styles.tabContainer}>
            <CustomText
              useTranslationText={false}
              text={"Course Settings"}
              size={isMedium ? "l" : "xs"}
              textType="display"
              weight="ultraBold"
              role="heading"
              aria-level="1"
              style={undefined}
            />
          </View>
        </View>
      </MotiView>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={500}
        transition={{
          all: { type: "timing", duration: 600 },
        }}>
        <CustomSpacing type="vertical" size="s" />
        {isAxioOffering ? (
          <TabNavigator
            tabData={tabs}
            activeTab={activeTab}
            onTabPress={(key) => {
              navigation.navigate("Course Settings", { courseId, tab: key });
            }}
            activeTabColor={primaryColor}
            rightButtonComponent={undefined}
          />
        ) : null}
        <CustomSpacing type="vertical" size="l" />
      </MotiView>
      {activeTab === "courseAnnouncements" ? <CourseAnnouncementsContainer courseId={courseId} /> : null}
      {activeTab === "courseInfo" ? <CourseInfoContainer courseId={courseId} /> : null}
      {activeTab === "rosterManagement" ? <RosterManagementContainer courseId={courseId} /> : null}
      {activeTab === "dueDateControls" ? <DueDateControlsContainer courseId={courseId} /> : null}
      {activeTab === "courseCatalogInfo" ? <CourseCatalogInfoContainer courseId={courseId} /> : null}
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  tabContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  breadcrumbText: {
    marginBottom: 20,
    color: "#6B7280",
    fontWeight: "500",
  },
});

export default CourseSettingsView;
